<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IonIcon } from '@ionic/vue';
import { linkOutline } from 'ionicons/icons';
import QrcodeVue from 'qrcode.vue';
import { useI18n } from 'vue-i18n';

import type { QueueSlot } from '@/modules/queue/composables/useQueue';

// Props
const props = defineProps({
	slot: { type: Object as () => QueueSlot, required: true },
	walletLinked: { type: Boolean, default: false },
	hideQrCode: { type: Boolean, default: false },
	showDisclaimer: { type: Boolean, default: false },
});

// Init
const { t, locale } = useI18n();
const hideQrCodeRef = ref(props.hideQrCode);
const qrCodeSizes = [150, 230, 80];
const qrCodeSize = ref(qrCodeSizes[0]); // Default size
const currentSizeIndex = ref(0);

// Watch for prop changes
watch(() => props.hideQrCode, (newValue) => {
	hideQrCodeRef.value = newValue;
});

const showQrCode = () => {
	hideQrCodeRef.value = false;
};

const cycleQrCodeSize = () => {
	currentSizeIndex.value = (currentSizeIndex.value + 1) % qrCodeSizes.length;
	qrCodeSize.value = qrCodeSizes[currentSizeIndex.value];
};

// Computed properties
const qrCodeData = computed(() => {
	// Use the actual QR code from backend if available, otherwise fall back to slot ID
	return props.slot.qr_code || `queue_ticket_${props.slot.id}`;
});

const isFastlane = computed(() => {
	return props.slot.queue_type === 'fastlane';
});

const isRepositioned = computed(() => {
	return props.slot.was_repositioned || props.slot.repositioned_at;
});

const repositionedMessage = computed(() => {
	if (!isRepositioned.value) return '';

	const activity = props.slot.queue_activity;
	if (activity?.queue_type === 'chronological') {
		return t('queue.repositioned_late_arrival', {
			original: props.slot.original_position_number || '?',
			current: props.slot.position_number
		});
	} else {
		return t('queue.repositioned_late_arrival_timeslot', {
			original: props.slot.original_booked_time_slot || '?',
			current: props.slot.booked_time_slot || ''
		});
	}
});

const userName = computed(() => {
	const user = props.slot.user;
	if (user?.full_name && user.full_name.trim()) {
		return user.full_name;
	}
	// Try to construct name from first_name and last_name if available
	if (user && (user as any).first_name || (user as any).last_name) {
		const firstName = (user as any).first_name?.trim() || '';
		const lastName = (user as any).last_name?.trim() || '';
		if (firstName || lastName) {
			return `${firstName} ${lastName}`.trim();
		}
	}
	// Fallback to guest
	return t('queue.guest');
});

const estimatedWaitTime = computed(() => {
	const minutes = props.slot.estimated_wait_minutes || 0;
	return Math.max(0, minutes);
});

const formattedWaitTime = computed(() => {
	const minutes = estimatedWaitTime.value;
	if (minutes < 60) {
		return `${minutes} ${t('queue.minutes')}`;
	}
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	if (remainingMinutes === 0) {
		return `${hours} ${hours > 1 ? t('queue.hours_plural') : t('queue.hour')}`;
	}
	return `${hours} ${hours > 1 ? t('queue.hours_plural') : t('queue.hour')} ${remainingMinutes} ${t('queue.minutes')}`;
});

// Get activity name
const activityName = computed(() => {
	return props.slot.queue_activity?.name || t('queue.activity');
});

// Get early arrival time (in minutes) - check metadata or use default
const earlyArrivalMin = computed(() => {
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;
	// Check if metadata has early_arrival_min, otherwise default to 5
	return activity?.metadata?.early_arrival_min || 5;
});

const earlyArrivalMax = computed(() => {
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;
	// Check if metadata has early_arrival_max, otherwise default to 10
	return activity?.metadata?.early_arrival_max || 10;
});

// Get grace period (buffer time) in minutes
const gracePeriodMinutes = computed(() => {
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;
	if (activity) {
		// Use buffer_time_override from sub_activity if available, otherwise use buffer_time_minutes from activity
		return props.slot.sub_activity?.buffer_time_override ?? activity.buffer_time_minutes ?? 5;
	}
	return 5; // Default fallback
});

// Get fastpass wait time (for normal queue tickets only)
const fastpassWaitTime = computed(() => {
	// Estimate fastpass would be ~15% of regular wait time
	const regularWait = estimatedWaitTime.value;
	return Math.max(1, Math.floor(regularWait * 0.15));
});

// Check if booked timeslot has passed
const isTimeslotPassed = computed(() => {
	// Only check for time_slot queue types
	if (!shouldShowBookedTimeslot.value) {
		return false; // Not a time slot booking, so not applicable
	}

	const timeslot = props.slot.booked_time_slot;
	const bookedDate = props.slot.booked_date;
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;

	if (!timeslot || !bookedDate) {
		return false; // No timeslot info, allow fastpass
	}

	try {
		// First, check if the booked date itself is in the past
		const bookedDateObj = new Date(bookedDate);
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset to start of today for comparison
		bookedDateObj.setHours(0, 0, 0, 0); // Reset to start of booked date for comparison

		// If booked date is before today, the timeslot has definitely passed
		if (bookedDateObj < today) {
			return true;
		}

		// If booked date is in the future, the timeslot hasn't passed
		if (bookedDateObj > today) {
			return false;
		}

		// If booked date is today, check if the end time has passed
		let endTime: Date | null = null;

		// Check if timeslot is a UTC datetime string (e.g., "2025-10-31T09:00:00.000000Z")
		const isUtcDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timeslot.trim());

		if (isUtcDateTime) {
			// Parse UTC datetime
			const utcDate = new Date(timeslot);
			if (!isNaN(utcDate.getTime())) {
				// Calculate end time from cycle duration
				if (activity && activity.cycle_duration_minutes) {
					endTime = new Date(utcDate.getTime() + activity.cycle_duration_minutes * 60000);
				} else {
					endTime = utcDate; // Use start time as end time if no cycle duration
				}
			}
		} else {
			// Parse time string format (e.g., "10:00", "10:00-11:00")
			const allTimeMatches = timeslot.match(/(\d{1,2}:\d{2})(?::\d{2})?/g);

			if (allTimeMatches && allTimeMatches.length >= 2) {
				// Has both start and end times (e.g., "10:00-11:00")
				const endTimeStr = allTimeMatches[allTimeMatches.length - 1].substring(0, 5);
				const [hours, minutes] = endTimeStr.split(':').map(Number);
				const slotDate = new Date(bookedDate);
				slotDate.setHours(hours, minutes, 0, 0);
				endTime = slotDate;
			} else if (allTimeMatches && allTimeMatches.length === 1) {
				// Single time found, calculate end time from cycle duration
				const startTimeStr = allTimeMatches[0].substring(0, 5);
				const [hours, minutes] = startTimeStr.split(':').map(Number);
				const slotDate = new Date(bookedDate);
				slotDate.setHours(hours, minutes, 0, 0);

				if (activity && activity.cycle_duration_minutes) {
					endTime = new Date(slotDate.getTime() + activity.cycle_duration_minutes * 60000);
				} else {
					endTime = slotDate; // Use start time as end time if no cycle duration
				}
			}
		}

		// Check if end time has passed (only if we're checking today's timeslot)
		if (endTime) {
			return endTime < new Date();
		}

		return false; // Couldn't parse timeslot, allow fastpass
	} catch (error) {
		console.error('Error checking if timeslot has passed:', error);
		return false; // On error, allow fastpass to be safe
	}
});

// Check if upsell is available and get price text
const upsellAvailable = computed(() => {
	const activity = props.slot.queue_activity;

	// Check if activity has upsell configured
	if (!activity || !activity.upsell_price || !activity.upsellCurrency) {
		return false;
	}

	// Check if fastlane is available (from backend response)
	// If fastlane_available is explicitly false, hide the upsell option
	if (props.slot.fastlane_available === false) {
		return false;
	}

	// Hide fastpass option if ticket is already used/completed
	if (isUsed.value) {
		return false;
	}

	// Hide fastpass option if timeslot has passed
	if (isTimeslotPassed.value) {
		return false;
	}

	// If fastlane_available is true or undefined (not provided), show the option
	// (undefined means we don't have availability info, so we show it to be safe)
	return true;
});

const isUsed = computed(() => {
	return props.slot.status === 'completed';
});

const upsellButtonText = computed(() => {
	if (!upsellAvailable.value) {
		return t('queue.buy_fastpass');
	}

	const activity = props.slot.queue_activity;
	const price = (activity!.upsell_price! / 100).toFixed(2);
	const currency = activity!.upsellCurrency!;
	return `${t('queue.buy_fastpass')} - ${currency.prefix}${price}${currency.suffix}`;
});

// Check if we should show booked timeslot instead of position
const shouldShowBookedTimeslot = computed(() => {
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;
	return activity?.queue_type === 'time_slot' && !!props.slot.booked_time_slot;
});

// Format booked date - use locale-aware formatting
const formattedBookedDate = computed(() => {
	if (!props.slot.booked_date) return '';
	try {
		const date = new Date(props.slot.booked_date);
		// Map i18n locale codes to Intl locale codes
		const localeMap: Record<string, string> = {
			'en': 'en-US',
			'es': 'es-ES',
			'fr': 'fr-FR',
			'pt': 'pt-PT',
			'nl': 'nl-NL'
		};
		const intlLocale = localeMap[locale.value] || locale.value || 'en-US';

		return date.toLocaleDateString(intlLocale, {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	} catch {
		return props.slot.booked_date;
	}
});

// Format booked timeslot - show as "10:00 - 10:30" format
const formattedBookedTimeslot = computed(() => {
	if (!props.slot.booked_time_slot) return '';

	const timeslot = props.slot.booked_time_slot;
	const activity = props.slot.queue_activity || props.slot.sub_activity?.queue_activity;

	// Check if timeslot is a UTC datetime string (e.g., "2025-10-31T09:00:00.000000Z")
	const isUtcDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timeslot.trim());

	if (isUtcDateTime) {
		// Parse UTC datetime and convert to local time
		try {
			const utcDate = new Date(timeslot);
			if (!isNaN(utcDate.getTime())) {
				// Extract local time
				const localHours = utcDate.getHours().toString().padStart(2, '0');
				const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
				const startTime = `${localHours}:${localMinutes}`;

				// Calculate end time from cycle duration
				if (activity && activity.cycle_duration_minutes) {
					const endDate = new Date(utcDate.getTime() + activity.cycle_duration_minutes * 60000);
					const endHours = endDate.getHours().toString().padStart(2, '0');
					const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
					const endTime = `${endHours}:${endMinutes}`;

					return `${startTime} - ${endTime}`;
				}

				return startTime;
			}
		} catch (error) {
			console.error('Error parsing UTC datetime:', error);
		}
	}

	// If timeslot is a simple time string like "10:00", use it directly
	// Check if it's already in HH:MM format (no date, no ISO datetime)
	const simpleTimePattern = /^(\d{1,2}:\d{2})(?::\d{2})?$/;
	const simpleMatch = timeslot.trim().match(simpleTimePattern);
	if (simpleMatch) {
		const startTime = simpleMatch[1]; // Already in HH:MM format

		// Calculate end time from cycle duration
		if (activity && activity.cycle_duration_minutes) {
			const [hours, minutes] = startTime.split(':').map(Number);
			const startDate = new Date();
			startDate.setHours(hours, minutes, 0, 0);

			// Add cycle duration
			const endDate = new Date(startDate.getTime() + activity.cycle_duration_minutes * 60000);

			const endHours = endDate.getHours().toString().padStart(2, '0');
			const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
			const endTime = `${endHours}:${endMinutes}`;

			return `${startTime} - ${endTime}`;
		}

		return startTime;
	}

	// If timeslot contains a date-time format, extract the time portion
	// Handle formats like "2025-10-31T10:00:00" or "10:00-11:00"
	const allTimeMatches = timeslot.match(/(\d{1,2}:\d{2})(?::\d{2})?/g);

	if (allTimeMatches && allTimeMatches.length >= 2) {
		// Already has both times (e.g., "10:00-11:00" or date-time format)
		// Use the last two matches to avoid date parts
		const startTime = allTimeMatches[allTimeMatches.length - 2].substring(0, 5); // Keep only HH:MM
		const endTime = allTimeMatches[allTimeMatches.length - 1].substring(0, 5); // Keep only HH:MM
		return `${startTime} - ${endTime}`;
	} else if (allTimeMatches && allTimeMatches.length === 1) {
		// Single time found in date-time string
		const startTime = allTimeMatches[0].substring(0, 5); // Keep only HH:MM

		// Calculate end time from cycle duration
		if (activity && activity.cycle_duration_minutes) {
			const [hours, minutes] = startTime.split(':').map(Number);
			const startDate = new Date();
			startDate.setHours(hours, minutes, 0, 0);

			// Add cycle duration
			const endDate = new Date(startDate.getTime() + activity.cycle_duration_minutes * 60000);

			const endHours = endDate.getHours().toString().padStart(2, '0');
			const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
			const endTime = `${endHours}:${endMinutes}`;

			return `${startTime} - ${endTime}`;
		}

		return startTime;
	}

	// Fallback: return as is
	return timeslot;
});
</script>

<template>
	<div class="queue-ticket-item w-[300px] h-[620px] flex flex-col">
		<!-- QR Code Section -->
		<div class="flex items-center justify-center h-[256px]">
			<div v-if="hideQrCodeRef" class="relative cursor-pointer" @click="showQrCode()">
				<div class="absolute flex items-center justify-center w-full h-full bg-black/50">
					<span class="uppercase font-sourcesanspro-semibold text-center">{{ t('components.ticket_detail_item.press_to_show') }}</span>
				</div>
				<qrcode-vue
					:value="qrCodeData"
					:level="'M'"
					:size="qrCodeSize"
					background="#E0E0E0"
					class="cursor-pointer transition-all duration-300"
					@click="cycleQrCodeSize" />
			</div>
			<div v-else>
				<qrcode-vue
					:value="qrCodeData"
					:level="'M'"
					:size="qrCodeSize"
					background="#E0E0E0"
					class="cursor-pointer transition-all duration-300"
					@click="cycleQrCodeSize" />
			</div>
		</div>

		<!-- Wallet Linked Badge -->
		<div v-if="walletLinked" class="absolute flex top-[238px] left-0 right-0 items-center justify-center">
			<div class="flex px-6 py-2 bg-black rounded-full font-sourcesanspro-semibolditalic text-xs2 text-white/70">
				<ion-icon :icon="linkOutline" class="mr-2 text-white"></ion-icon>
				{{ t('components.ticket_detail_item.your_wallet_linked') }}
			</div>
		</div>

		<!-- User Name -->
		<div class="flex mt-6 items-center justify-center mb-6">
			<div class="flex items-center justify-center font-sourcesanspro-semibold text-dark-blue">
				<span class="text-l2">{{ userName }}</span>
			</div>
		</div>

		<!-- Fastlane Badge (for fastlane tickets) -->
		<div v-if="isFastlane" class="flex items-center justify-center mb-2 px-6">
			<div class="flex h-[37px] px-14 py-2 items-center justify-center bg-[#f9af21] rounded-md">
				<span class="font-sourcesanspro-semibold text-base2 text-white">
					⭐️ Fastlane ⭐️
				</span>
			</div>
		</div>

		<!-- Repositioned Badge (for late arrivals) -->
		<div v-if="isRepositioned" class="flex items-center justify-center mb-2 px-6">
			<div class="flex px-6 py-2 items-center justify-center bg-orange-500 rounded-md">
				<span class="font-sourcesanspro-semibold text-sm text-white text-center">
					⏰ {{ repositionedMessage }}
				</span>
			</div>
		</div>

		<!-- Used/Scanned Badge (for completed tickets) -->
		<div v-if="isUsed" class="flex items-center justify-center mb-2 px-6">
			<div class="flex px-6 py-2 items-center justify-center bg-gray-600 rounded-md">
				<span class="font-sourcesanspro-semibold text-sm text-white text-center">
					✓ {{ t('queue.ticket_used') }}
				</span>
			</div>
		</div>

		<!-- Queue Information -->
		<div class="px-6 pb-4 text-dark-blue/70 flex flex-col flex-1">
			<!-- Estimated Wait Time (only for chronological queues) -->
			<div v-if="!shouldShowBookedTimeslot" class="flex flex-col items-center justify-center mb-2">
				<span class="font-sourcesanspro-semibold text-sm text-dark-blue">
					{{ t('queue.estimated_wait_time') }}
				</span>
				<span class="font-sourcesanspro-bold text-sm text-dark-blue">
					{{ formattedWaitTime }}
				</span>
			</div>

			<!-- Position or Booked Timeslot -->
			<div v-if="!shouldShowBookedTimeslot" class="flex flex-col items-center justify-center mb-3">
				<span class="font-sourcesanspro-semibold text-sm text-dark-blue">
					{{ t('queue.position') }}
				</span>
				<span class="font-sourcesanspro-bold text-[48px] leading-[48px] text-dark-blue">
					#{{ slot.position_number }}
				</span>
			</div>

			<!-- Booked Timeslot and Date -->
			<div v-else class="flex flex-col items-center justify-center mb-3 px-2">
				<!-- Activity Name Badge -->
				<div class="flex items-center justify-center bg-dark-blue/10 rounded-lg px-4 py-2 w-full max-w-[240px] mb-2">
					<span class="font-sourcesanspro-bold text-lg text-dark-blue">
						{{ activityName }}
					</span>
				</div>

				<!-- Timeslot Badge -->
				<div class="flex items-center justify-center bg-dark-blue/10 rounded-lg px-4 py-2 w-full max-w-[240px] mb-2">
					<span class="font-sourcesanspro-bold text-lg text-dark-blue">
						{{ formattedBookedTimeslot }}
					</span>
				</div>

				<!-- Date Badge - Combine with timeslot if available -->
				<div v-if="formattedBookedDate" class="flex items-center justify-center bg-dark-blue/10 rounded-lg px-4 py-1.5 w-full max-w-[240px]">
					<span class="font-sourcesanspro-semibold text-xs text-dark-blue">
						{{ formattedBookedDate }}
					</span>
				</div>
			</div>

			<!-- Fastpass Option (for normal queue tickets only) -->
			<div v-if="!isFastlane && upsellAvailable" class="flex flex-col items-center justify-center mb-2">
				<span class="font-sourcesanspro-regular text-sm text-dark-blue text-center">
					{{ t('queue.or_fastpass', { minutes: fastpassWaitTime }) }}
				</span>
			</div>

			<!-- Buy Fastpass Button (for normal queue tickets only) -->
			<div v-if="!isFastlane && upsellAvailable" class="flex items-center justify-center mb-2">
				<button
					class="buy-fastpass-btn"
					@click="$emit('buyFastpass', slot.id)">
					{{ upsellButtonText }}
				</button>
			</div>

			<!-- Warning Text (only shown on queue tickets page) -->
			<div v-if="showDisclaimer" class="flex flex-col items-center justify-center text-center mt-auto">
				<p class="font-sourcesanspro-light text-[10px] text-dark-blue leading-tight mb-0">
					{{ t('queue.check_in_warning_1', { activity: activityName, min: earlyArrivalMin, max: earlyArrivalMax }) }}
				</p>
				<p v-if="t('queue.check_in_warning_2')" class="font-sourcesanspro-light text-[10px] text-dark-blue leading-tight mb-0">
					{{ t('queue.check_in_warning_2', { min: earlyArrivalMin, max: earlyArrivalMax }) }}
				</p>
				<p class="font-sourcesanspro-light text-[10px] text-dark-blue leading-tight mb-0">
					{{ t('queue.check_in_warning_3', { minutes: gracePeriodMinutes }) }}
				</p>
				<p class="font-sourcesanspro-light text-[10px] text-dark-blue leading-tight">
					{{ t('queue.check_in_warning_4') }}
				</p>
			</div>
		</div>
	</div>
</template>

<style scoped>
.queue-ticket-item {
	background: url('@/statics/svg/ticket-detail-item.svg') no-repeat;
	background-size: cover;
	position: relative;
}

.buy-fastpass-btn {
	background: #27c840;
	color: white;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 14px;
	padding: 10px 24px;
	border-radius: 62px;
	border: none;
	cursor: pointer;
	transition: background 0.2s ease;
}

.buy-fastpass-btn:hover {
	background: #22b238;
}

.buy-fastpass-btn:active {
	background: #1da030;
}
</style>
